// Data layer for the customer PdM page (issue #11). Entitlement, the latest
// scheduled report, and the model score trend — all company-scoped reads of
// the api's /v1/pdm routes (gated server-side on the subscription row).
// Convention per view-models/README.md: hooks + pure adapters, no JSX.

import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { API_BASE } from '@/apiConfig';
import { withCompany } from '@/lib/withCompany';

export interface PdmSubsystemHealth {
  score: number | null;
  status: string;
  weakest_domain: string | null;
}
export interface PdmDomainHealth {
  score: number | null;
  status: string;
  onsets: number;
  events: number;
  risk_fraction: number | null;
  risk_status: string | null;
}
export interface PdmSensorDeviation {
  sensor: string;
  state: string;
  recent_mean: number;
  self_z: number | null;
  fleet_z: number | null;
  peer_z?: number | null; // vs sibling sensors (e.g. the optic's other zones)
  basis: string;
  /** Customer-action sentence from the engine's single advice source. */
  advice?: string;
}
export interface PdmMachineHealth {
  machine: string;
  overall_score: number | null;
  overall_status: string;
  weakest_domain: string | null;
  subsystems: Record<string, PdmSubsystemHealth>;
  domains: Record<string, PdmDomainHealth>;
  sensor_watch?: { score: number | null; status: string; deviations: PdmSensorDeviation[] };
  data_quality: { score: number; status: string; note?: string | null };
}
export interface PdmMatrixCell {
  state: string;
  recent_mean: number;
  recent_min?: number | null;
  recent_max?: number | null;
  self_z: number | null;
  fleet_z: number | null;
  peer_z: number | null;
  basis: string;
  /** Which way is bad for this sensor (registry-driven). */
  direction?: string;
  /** Direction-gated comparative deviation in σ — THE triage key. */
  severity?: number;
  /** Engine-owned severity bucket (issue #14). */
  bucket?: PdmBucket;
  /** Which side of the band the deviation sits on. */
  side?: 'high' | 'low' | null;
  /** False when the sensor isn't judged in this state (issue #17) — the cell
   *  shows its value but is muted, generates no finding, no score impact. */
  scored?: boolean;
}
export type PdmBucket = 'ok' | 'watch' | 'warn' | 'critical';
export interface PdmFinding {
  machine: string;
  sensor: string;
  label?: string;
  state: string;
  recent_mean: number;
  recent_min?: number | null;
  recent_max?: number | null;
  severity: number;
  bucket: PdmBucket;
  direction?: string;
  side?: 'high' | 'low' | null;
  trend?: 'new' | 'worsening' | 'improving' | 'stable';
  delta_sigma?: number;
  spark?: number[];
}
export interface PdmSensorMatrix {
  sensors: { sensor: string; label?: string; group: string | null; direction?: string }[];
  states: string[]; // operating states present (Idle / Ready / Running / …)
  cells: Record<string, Record<string, Record<string, PdmMatrixCell>>>; // machine → sensor → state → cell
}
export interface PdmReportPayload {
  id: string;
  created_at: string;
  period_from: string;
  period_to: string;
  sent_at: string | null;
  report: {
    machine_health: (PdmMachineHealth & { score_delta?: number })[];
    window_days?: number;
    sensor_matrix?: PdmSensorMatrix;
    findings?: PdmFinding[];
  };
  scores?: {
    model_version: string;
    horizon_min: number;
    scores: { machine: string; probability: number | null }[];
  };
}

export function matrixDeviation(c: PdmMatrixCell): number {
  // severity = engine's direction-gated comparative deviation (issue #12);
  // comparative-|z| fallback for old reports.
  if (c.severity != null) return c.severity;
  return Math.max(Math.abs(c.peer_z ?? 0), Math.abs(c.fleet_z ?? 0));
}

/** Quiet Triage bucket (issue #14): engine value preferred, derived fallback
 *  for older payloads. */
export function cellBucket(c: PdmMatrixCell): PdmBucket {
  if (c.bucket) return c.bucket;
  const s = matrixDeviation(c);
  return s >= 3 ? 'critical' : s >= 2 ? 'warn' : s >= 1 ? 'watch' : 'ok';
}

/** Dark-theme bucket tokens — healthy is QUIET, no green anywhere; markers
 *  double the color signal for colorblind readers. */
export const BUCKET_STYLE: Record<PdmBucket, { bg: string; fg: string; weight: number }> = {
  ok:       { bg: 'transparent',           fg: 'var(--fg-primary)', weight: 400 },
  watch:    { bg: 'rgba(245,158,11,.12)',  fg: '#fbbf24',           weight: 500 },
  warn:     { bg: 'rgba(245,158,11,.30)',  fg: '#fcd34d',           weight: 600 },
  critical: { bg: 'rgba(239,68,68,.30)',   fg: '#fca5a5',           weight: 700 },
};

/** Directional marker: ▲ above band, ▼ below band, doubled at critical.
 *  side from the engine; direction fallback for older payloads. */
export function markerFor(bucket: PdmBucket, side?: 'high' | 'low' | null, direction?: string): string {
  if (bucket === 'ok') return '';
  const m = side === 'low' || (!side && direction === 'low_bad') ? '▼' : '▲';
  return bucket === 'critical' ? m + m : m;
}
export interface PdmScoreRecord {
  scored_at: string;
  machine: string;
  model_version: string;
  probability: number | null;
}

async function get<T>(token: string, url: string): Promise<T> {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = ((await res.json()) as { error?: string }).error ?? msg; } catch { /* non-JSON */ }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
}

export function usePdmEntitlement(gate: boolean = true) {
  const token = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);
  return useQuery({
    queryKey: ['pdm-entitlement', company],
    // `gate` lets callers skip the request entirely when the user lacks the
    // pdm-page flag — the api would 404 it anyway. `company` gate: the endpoint
    // is company-scoped and 400s without one (an admin who hasn't picked a
    // company yet), so never fire it company-less.
    enabled: !!token && gate && !!company,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: () => get<{ enabled: boolean }>(token!, withCompany(`${API_BASE}/pdm/entitlement`, company)),
  });
}

export function usePdmLatestReport(enabled: boolean) {
  const token = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);
  return useQuery({
    queryKey: ['pdm-report', company],
    enabled: !!token && enabled,
    queryFn: () => get<PdmReportPayload>(token!, withCompany(`${API_BASE}/pdm/report/latest`, company)),
    retry: false, // 404 = no report yet, not a transient failure
  });
}

export function usePdmScoreTrend(enabled: boolean) {
  const token = useAuthStore((s) => s.user?.token ?? null);
  const company = useAuthStore((s) => s.company);
  return useQuery({
    queryKey: ['pdm-scores', company],
    enabled: !!token && enabled,
    queryFn: () => get<PdmScoreRecord[]>(token!, withCompany(`${API_BASE}/pdm/scores`, company)),
  });
}

/** Group score records into an ASC-time series per machine (records arrive
 *  newest-first from the api). Pure — unit-testable without fetch. */
export function trendByMachine(records: PdmScoreRecord[]): Map<string, { t: number; p: number }[]> {
  const out = new Map<string, { t: number; p: number }[]>();
  for (const r of records) {
    if (r.probability == null) continue;
    const arr = out.get(r.machine) ?? [];
    arr.push({ t: Date.parse(r.scored_at), p: r.probability });
    out.set(r.machine, arr);
  }
  for (const arr of out.values()) arr.sort((a, b) => a.t - b.t);
  return out;
}

/** Latest probability per machine from the report's embedded snapshot. */
export function probabilityByMachine(payload: PdmReportPayload | undefined): Map<string, number> {
  const out = new Map<string, number>();
  for (const s of payload?.scores?.scores ?? []) {
    if (s.probability != null) out.set(s.machine, s.probability);
  }
  return out;
}
